import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  X,
  Target,
  Users,
  Sparkles,
  ExternalLink,
  MessageCircle,
} from 'lucide-react';
import { BriefCreatorOverlay } from '@/components/brief/BriefCreatorOverlay';
import { clsx } from 'clsx';
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  creativeWorkbenchApi,
  type WorkbenchAdDetailResponse,
  type WorkbenchVerdict,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

const VERDICT_COLOR: Record<WorkbenchVerdict, string> = {
  strong: 'bg-emerald-100 text-emerald-700',
  promising: 'bg-teal-100 text-teal-700',
  needs_work: 'bg-amber-100 text-amber-700',
  misaligned: 'bg-red-100 text-red-700',
};

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

interface Props {
  adId: string;
  onClose: () => void;
  onDrillDown: (seed: ChatSeedInput) => void;
}

export function AdDetailDrawer({ adId, onClose, onDrillDown }: Props) {
  const navigate = useNavigate();
  const { clientUid, personaId } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchAdDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [backfillOpen, setBackfillOpen] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getAdDetail(adId, { clientUid })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, adId]);

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-end pointer-events-none">
      <div
        className="absolute inset-0 bg-slate-900/30 pointer-events-auto"
        onClick={onClose}
      />
      <aside className="relative bg-white border-l border-slate-200 w-full max-w-md h-full overflow-y-auto pointer-events-auto shadow-2xl">
        <header className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase text-slate-400">
              Ad detail
            </div>
            <h2
              className="text-sm font-bold text-navy-900 truncate"
              title={data?.ad_name ?? adId}
            >
              {data?.ad_name ?? adId}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
          >
            <X size={16} />
          </button>
        </header>

        {loading && !data && (
          <div className="p-6 text-xs text-slate-400">Loading ad detail...</div>
        )}

        {data && !data.found && (
          <div className="p-6 text-xs text-slate-400">
            No detail data for this ad.
          </div>
        )}

        {data?.found && (
          <div className="p-4 space-y-4">
            {/* Fit verdict */}
            {data.fit && (
              <section className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase text-slate-400">
                    Verdict
                  </span>
                  <span
                    className={clsx(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                      VERDICT_COLOR[data.fit.verdict],
                    )}
                  >
                    <Sparkles size={10} />
                    {data.fit.verdict.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-3xl font-bold text-navy-900 tabular-nums">
                    {data.fit.fit_score.toFixed(1)}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    /10 persona-fit
                  </span>
                </div>
                <div className="text-[11px] text-slate-600">
                  Targeted at{' '}
                  <span className="font-bold text-navy-900">
                    {data.fit.target_persona ?? '—'}
                  </span>
                </div>
              </section>
            )}

            {/* Performance */}
            {data.performance && (
              <section className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Spend" value={CURRENCY.format(data.performance.spend)} />
                <Stat
                  label="Purchases"
                  value={data.performance.purchases.toLocaleString()}
                />
                <Stat
                  label="LTV ROAS"
                  value={`${data.performance.ltv_roas.toFixed(2)}x`}
                  accent
                />
                <Stat label="CTR" value={`${(data.performance.ctr * 100).toFixed(2)}%`} />
                <Stat label="CPM" value={`$${data.performance.cpm.toFixed(2)}`} />
                <Stat
                  label="Frequency"
                  value={data.performance.frequency.toFixed(2)}
                />
              </section>
            )}

            {/* Daily series */}
            {data.series && data.series.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-[10px] font-mono uppercase text-slate-400">
                  Daily Spend
                </h3>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} />
                      <YAxis stroke="#94a3b8" fontSize={9} />
                      <Tooltip
                        wrapperClassName="text-xs"
                        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        stroke="#14b8a6"
                        fill="#14b8a6"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {/* Delivery */}
            {data.delivery && (
              <section className="space-y-2">
                <h3 className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1">
                  <Users size={10} />
                  Delivery by Persona
                </h3>
                <div className="text-[11px] text-slate-500 mb-1">
                  {data.delivery.on_target_visitors} of {data.delivery.attributed_visitors}{' '}
                  attributed visitors landed on the targeted persona{' '}
                  {data.delivery.attributed_visitors > 0 && (
                    <span className="font-bold text-navy-900">
                      ({((data.delivery.on_target_visitors / data.delivery.attributed_visitors) * 100).toFixed(0)}%)
                    </span>
                  )}
                  .
                </div>
                <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                  {data.delivery.personas.map((p) => (
                    <li
                      key={p.persona_name}
                      className={clsx(
                        'flex items-center justify-between text-[11px] px-2 py-1.5',
                        p.is_target && 'bg-emerald-50',
                      )}
                    >
                      <span className="flex items-center gap-1 text-navy-900 truncate">
                        {p.is_target && <Target size={10} className="text-emerald-600" />}
                        {p.persona_name}
                      </span>
                      <span className="font-mono text-slate-500">
                        {p.visitors} visit · {p.purchasers} buy · ${p.avg_ltv.toFixed(0)} LTV
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Ontology */}
            {data.ontology && (
              <section className="space-y-2">
                <h3 className="text-[10px] font-mono uppercase text-slate-400">
                  Creative Formula
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {(['hook_type', 'tone', 'visual_style', 'cta_style'] as const).map(
                    (k) => (
                      <div
                        key={k}
                        className="bg-slate-50 border border-slate-100 rounded-lg p-2"
                      >
                        <div className="text-[9px] font-mono uppercase text-slate-400">
                          {k.replace('_', ' ')}
                        </div>
                        <div className="text-xs text-navy-900 font-bold">
                          {data.ontology?.[k] ?? '—'}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </section>
            )}

            {/* Actions */}
            <footer className="flex flex-col gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={() =>
                  onDrillDown({
                    question: `Why is ${data.ad_name} performing the way it is for ${data.fit?.target_persona ?? 'this persona'}?`,
                    chart_axis: { ad_id: adId, series: 'ad_review' },
                  })
                }
                className="flex items-center justify-center gap-1 text-xs bg-teal-500 hover:bg-teal-600 text-white rounded-lg py-2"
              >
                <MessageCircle size={12} />
                Walk through with the analyst
              </button>
              <button
                onClick={() => setBackfillOpen(true)}
                className="flex items-center justify-center gap-1 text-xs text-sky-600 hover:text-sky-800"
                title="Reverse-engineer a creative brief from this ad"
              >
                <ArrowLeft size={12} />
                Back-fill brief from this ad
              </button>
              <button
                onClick={() =>
                  navigate(
                    `/creative/assets/creative/${encodeURIComponent(data.creative_id ?? adId)}`,
                  )
                }
                className="flex items-center justify-center gap-1 text-xs text-slate-600 hover:text-teal-600"
              >
                <ExternalLink size={12} />
                Full analysis →
              </button>
            </footer>
          </div>
        )}
      </aside>

      {/* Phase 1 — back-fill brief from this ad */}
      <BriefCreatorOverlay
        open={backfillOpen}
        onClose={() => setBackfillOpen(false)}
        onBriefCreated={(briefId) => {
          setBackfillOpen(false);
          navigate(`/creative/briefs/${briefId}?lens=strategy`);
        }}
        defaultClientUid={clientUid || undefined}
        defaultPersonaId={personaId || undefined}
        defaultIntent="from_ad"
        defaultAdId={adId}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
      <div className="text-[9px] font-mono uppercase text-slate-400">{label}</div>
      <div
        className={clsx(
          'text-sm font-bold tabular-nums',
          accent ? 'text-teal-600' : 'text-navy-900',
        )}
      >
        {value}
      </div>
    </div>
  );
}
